import { BORDERS, COLORS, SHADOWS, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Body,
  Border,
  Column,
  DEPRECATED_Toast,
  Description,
  H3,
  InlineAlert,
  MdPadding,
  MdSpacing,
  XsSpacing,
  useScreenSize,
} from '@alto/design-system';
import {
  CartFixedFooter,
  CartMedicationItem,
  EditMedicationActionSheet,
  MissingNeedlesReminder,
  ORDER_TYPE_CONTEXT,
  OrderBundlingActionSheet,
  PaymentSummary,
  useOrderBundlingOptions,
} from '@alto/features';
// eslint-disable-next-line @alto/prefer-alto-features-shortform
import { useRemoveFromCart } from '@alto/features/src/cart/hooks/useRemoveFromCart';
// eslint-disable-next-line @alto/prefer-alto-features-shortform
import { useInitializeNewOrder } from '@alto/features/src/checkout/hooks/useInitializeNewOrder';
// eslint-disable-next-line @alto/prefer-alto-features-shortform
import { useProgynyWizard } from '@alto/features/src/checkout/hooks/useProgynyWizard';
// eslint-disable-next-line @alto/prefer-alto-features-shortform
import { useEssentialsCartThreshold } from '@alto/features/src/essentials/hooks/useEssentialsCartThreshold';
import { useNavigation } from '@alto/navigation';
import { CheckoutFlowTypeMap } from '@alto/scriptdash/alto/patient_app/checkout_flow/types/v1/checkout_flow_type';
import {
  type EnterCartData,
  type RequireNeedlePrescription,
} from '@alto/scriptdash/alto/patient_app/checkout_flow/v1/checkout_flow_endpoint';
import { OrderBundlingOriginMap } from '@alto/scriptdash/alto/patient_app/scheduling/order_bundling/types/v1/order_bundling_origin';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components/native';
// eslint-disable-next-line import/no-deprecated
import { fetchEnterCart, setWasNeedlePromptShown, updateOrder } from '~shared/actions/checkout';
import { deselectPrescription, selectPrescription } from '~shared/actions/prescriptions';
import { closeCart as dispatchCloseCart } from '~shared/actions/ui/cart';
import { clearSelectedPaymentTypes as clearUISelectedPaymentTypes } from '~shared/actions/ui/priceOptions';
import { PROGYNY_BILLING_DISCLAIMER } from '~shared/constants';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { useCartUsers } from '~shared/features/cart/hooks/useCartUsers';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { useCheckoutCourierTipping } from '~shared/features/checkout/hooks/useCheckoutCourierTipping';
import { getEnterCartData } from '~shared/features/checkout/selectors/getCheckoutFlow';
import { getDefaultAddress } from '~shared/features/checkout/selectors/getDefaultAddress';
import getShouldShowNoNeedlesWarning from '~shared/features/checkout/selectors/getShouldShowNoNeedlesWarning';
import type { ItemKey } from '~shared/features/checkout/types';
import { getErrorMessage } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useCartViewed } from '~shared/lib/analytics/src/hooks/useCartViewed';
import { useOrderPricing } from '~shared/queries';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { CartItemHeader } from './CartItemHeader';
import { AddNeedlesActionSheet } from '~web/features/my-meds/components/med-list/AddNeedlesActionSheet';
import { MissingNeedlesActionSheet } from '~web/features/my-meds/components/med-list/MissingNeedlesActionSheet';

const Container = styled(Column)<{ isSMScreenOrBigger: boolean }>`
  position: absolute;
  left: unset;
  height: 90vh;
  margin: 0;
  top: 55px;
  right: 0;
  min-width: calc(400px - ${SPACING.STATIC.XXL.rem});
  ${({ isSMScreenOrBigger }) =>
    !isSMScreenOrBigger &&
    `
      position: fixed;
      margin: 0;
      background: ${COLORS.BACKGROUND_COLORS.WHITE};
      top: 60px;
      width: 100vw;
      height: calc(100vh - 60px);
      min-width: unset;
    `}
`;

const Content = styled(Column)<{ isSMScreenOrBigger: boolean }>`
  max-height: calc(100% - ${SPACING.STATIC.MD.rem} * 2);
  background-color: ${COLORS.BACKGROUND_COLORS.WHITE};
  border-radius: ${BORDERS.RADIUS.LG.px};
  ${SHADOWS.BOTTOM}
  display: flex;
  margin: ${SPACING.STATIC.MD.px} auto;
  margin-top: ${SPACING.STATIC.MD.px};
  ${({ isSMScreenOrBigger }) =>
    !isSMScreenOrBigger &&
    `
      margin: ${SPACING.STATIC.XS.px};
      width: unset;
      min-width: unset;
    `}
`;

const ScrollableContent = styled(Column)`
  flex-wrap: nowrap;
  flex-direction: column;
  overflow-y: auto;
  padding: ${SPACING.STATIC.MD.px};
`;

const Sticky = styled(Column)<{ position: 'top' | 'bottom' }>`
  position: sticky;
  ${({ position }) => (position === 'top' ? 'top: 0;' : 'bottom: 0;')}
  z-index: 1;
`;

const DeviceContainer = styled(Column)<{ isSMScreenOrBigger: boolean }>`
  height: ${({ isSMScreenOrBigger }) => (isSMScreenOrBigger ? '100vh' : '100%')};
  display: initial;
`;

export const Cart = () => {
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { closeActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);
  const { trackEvent } = useAnalytics();
  useCartViewed();

  // Selectors and hooks
  const fetchEnterCheckoutError = useSelectorShared((state) => state.ui.errors.fetchEnterCheckoutError);
  const address = useSelectorShared(getDefaultAddress);
  const shouldShowNoNeedlesWarning = useSelectorShared(getShouldShowNoNeedlesWarning);
  const enterCartData = useSelectorShared(getEnterCartData);
  const { isOrderPricingLoading, orderPricing } = useOrderPricing();
  const { deliveryMethods } = useCartDeliveryMethods();
  const { initializeCartItems } = useInitializeNewOrder();
  const { handleRemoveFromCart } = useRemoveFromCart();
  const { isPhotoIDRequired } = useCartUsers();
  const { cartItems, cartSize } = useCartData();
  const { hasItemsBilledToProgyny, shouldShowPriceSummary } = useProgynyWizard();
  const hasSplitShipment = deliveryMethods.length > 1;
  const { earliestUpcomingShipment, cartShipment } = useOrderBundlingOptions({
    address_id: address?.id,
    origin: OrderBundlingOriginMap.ENTER_CHECKOUT,
  });
  const { showEssentialsThreshold, minimumAlertCopy, minimumThreshold } = useEssentialsCartThreshold();
  const { isEligibleForTipping: isTippable } = useCheckoutCourierTipping();
  const { isSMScreenOrBigger } = useScreenSize();
  const prevNumCartItems = useRef(0);

  // Memoized derived values
  const noNeedlesMedications = React.useMemo(() => {
    const requireNeedlePrescriptions =
      enterCartData?.[CheckoutFlowTypeMap.NEEDLE_PROMPT_NO_NEEDLES]?.require_needle_prescriptions ?? [];
    return requireNeedlePrescriptions.map((rx: RequireNeedlePrescription) => rx.medication_name);
  }, [enterCartData]);

  // Close cart when all items are removed
  const closeCart = useCallback(() => {
    dispatch(dispatchCloseCart());
  }, [dispatch]);

  useEffect(() => {
    if (prevNumCartItems.current > 0 && cartSize === 0) closeCart();
    prevNumCartItems.current = cartSize;
  }, [cartSize, closeCart]);

  // Remove tip if all tippable items removed
  useEffect(() => {
    if (!isTippable) {
      dispatch(updateOrder({ tip_amount: undefined }));
    }
  }, [cartSize, dispatch, isTippable]);

  // Edit medication flow handlers
  const closeEditMedicationFlow = useCallback(() => {
    dispatch(deselectPrescription());
    dispatch(clearUISelectedPaymentTypes());
  }, [dispatch]);

  const openEditMedicationFlow = useCallback(
    ({ itemKey, prescriptionID }: { itemKey: ItemKey; prescriptionID: number }) => {
      const handleRemoveFromCartByItemKey = () => {
        closeEditMedicationFlow();
        closeActionSheet();
        handleRemoveFromCart({ item: itemKey, properties: { source: 'cart_summary' } });
        if (cartSize === 1) closeCart();
      };

      dispatch(selectPrescription(prescriptionID));
      setActiveActionSheet(
        <EditMedicationActionSheet
          onClose={closeEditMedicationFlow}
          onRemoveMedication={handleRemoveFromCartByItemKey}
          orderTypeContext={ORDER_TYPE_CONTEXT.ADD_TO_CART}
        />,
      );
    },
    [
      dispatch,
      setActiveActionSheet,
      closeEditMedicationFlow,
      closeActionSheet,
      handleRemoveFromCart,
      cartSize,
      closeCart,
    ],
  );

  // Handle checkout modal logic
  const handleCheckoutModal = useCallback(
    (data: EnterCartData) => {
      if (data.checkout_flow_types.includes(CheckoutFlowTypeMap.NEEDLE_PROMPT_NO_NEEDLES)) {
        dispatch(setWasNeedlePromptShown(true));
        setActiveActionSheet(<MissingNeedlesActionSheet />);
      } else if (data.checkout_flow_types.includes(CheckoutFlowTypeMap.NEEDLE_PROMPT)) {
        dispatch(setWasNeedlePromptShown(true));
        setActiveActionSheet(<AddNeedlesActionSheet />);
      } else if (earliestUpcomingShipment && cartShipment) {
        setActiveActionSheet(<OrderBundlingActionSheet />);
      } else {
        navigate('RouteCheckout');
      }
    },
    [cartShipment, dispatch, earliestUpcomingShipment, navigate, setActiveActionSheet],
  );

  const handleCheckout = useCallback(async () => {
    initializeCartItems();
    trackEvent({ event: EVENTS.CART__CHECKOUT_TAPPED });
    if (isPhotoIDRequired) {
      trackEvent({
        event: EVENTS.SHOW_PHOTO_ID_UPLOAD_MODAL,
        params: { origin: 'cart' },
      });
      navigate('RoutePhotoId');
    } else {
      // eslint-disable-next-line import/no-deprecated
      await dispatch(fetchEnterCart()).then((response) => {
        // eslint-disable-next-line promise/always-return
        if (response.data) handleCheckoutModal(response.data);
      });
    }
    closeCart();
  }, [closeCart, dispatch, handleCheckoutModal, initializeCartItems, isPhotoIDRequired, navigate, trackEvent]);

  return (
    <Container isSMScreenOrBigger={isSMScreenOrBigger}>
      <DeviceContainer isSMScreenOrBigger={isSMScreenOrBigger}>
        <Content
          isSMScreenOrBigger={isSMScreenOrBigger}
          wrap={false}
          style={{
            borderLeftWidth: BORDERS.SIZE,
            borderRightWidth: BORDERS.SIZE,
            borderStyle: BORDERS.STYLE.SOLID,
            borderWidth: BORDERS.SIZE,
            borderColor: COLORS.BORDER_COLORS.LIGHT,
          }}
        >
          <Sticky
            position="top"
            flexShrink={0}
          >
            <CartItemHeader onClose={closeCart} />
          </Sticky>
          <ScrollableContent>
            {cartSize === 0 && <Description> You don't have anything in your cart.</Description>}
            {showEssentialsThreshold && (
              <MdPadding>
                <InlineAlert type="warning">
                  <H3
                    color={COLORS.TEXT_COLORS.BLACK}
                    fontFamily="bold"
                  >
                    ${minimumThreshold} minimum subtotal
                  </H3>
                  <XsSpacing />
                  <Description>{minimumAlertCopy}</Description>
                </InlineAlert>
              </MdPadding>
            )}
            {!!fetchEnterCheckoutError && (
              <DEPRECATED_Toast error>
                <Body>{getErrorMessage(fetchEnterCheckoutError)}</Body>
              </DEPRECATED_Toast>
            )}
            {shouldShowNoNeedlesWarning && (
              <>
                <MissingNeedlesReminder medication_names={noNeedlesMedications} />
                <MdSpacing />
              </>
            )}
            {cartItems.map((cartItem) =>
              cartItem.resource_type === 'Prescription' ? (
                <CartMedicationItem
                  key={cartItem.resource_id}
                  cartItem={cartItem}
                  openEditMedicationFlow={openEditMedicationFlow}
                />
              ) : null,
            )}
            {hasItemsBilledToProgyny && (
              <>
                <InlineAlert type="warning">
                  <Description fontFamily="bold">{PROGYNY_BILLING_DISCLAIMER.TITLE}</Description>
                  <Description>{PROGYNY_BILLING_DISCLAIMER.BODY}</Description>
                </InlineAlert>
                <MdSpacing />
              </>
            )}
            {shouldShowPriceSummary && !hasSplitShipment && !isOrderPricingLoading && orderPricing && (
              <Column flexShrink={0}>
                <PaymentSummary origin="Cart" />
                <MdSpacing />
              </Column>
            )}
          </ScrollableContent>
          <Sticky
            position="bottom"
            flexShrink={0}
          >
            <Border
              hideBottom
              hideLeft
              hideRight
              noTopLeftRadius
              noTopRightRadius
            >
              <MdPadding
                style={{
                  borderBottomLeftRadius: BORDERS.RADIUS.LG.value,
                  borderBottomRightRadius: BORDERS.RADIUS.LG.value,
                }}
              >
                <CartFixedFooter
                  visible={cartSize > 0}
                  handleCheckout={handleCheckout}
                  key="cart-fixed-footer"
                />
              </MdPadding>
            </Border>
          </Sticky>
        </Content>
      </DeviceContainer>
    </Container>
  );
};
